import { Stack, useTheme } from "@mui/material";
import { Box } from "lucide-react";
import type { FC } from "react";
import { usePortfolioDocumentsQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioDocumentsQuery";
import {
	ClientPortfolioDataCommodityServiceEnum,
	type ServiceDetailDataDTO,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DetailSectionLoader } from "web_myplann/Portfolio/Components/DetailSectionLoader";
import { RelatedSection } from "web_myplann/Portfolio/Components/RelatedSection";
import { computeRelatedPresence } from "web_myplann/Portfolio/Helpers/getHasRelated";
import { getPortfolioFrequencyValue } from "web_myplann/Portfolio/Helpers/utils";
import {
	DataCard,
	DataCardPreset,
} from "web_myplann/Shared/Components/DataCard";
import { DataRow } from "web_myplann/Shared/Components/DataRow";
import {
	dateFormatter,
	moneyFormatter,
} from "web_myplann/Shared/Utils/formatters";
import { EMPTY_DETAIL_FIELD } from "web_myplann/Utils/constants";
import type { PortfolioDetailProps } from "web_myplann/Utils/types";

const PageResources = Resources.PortfolioDetail.Services;

export const Services: FC<PortfolioDetailProps> = ({
	baseData,
	currencySign,
}) => {
	const { t } = useResource();
	const theme = useTheme();

	const { data: documents, isLoading: isDocumentsLoading } =
		usePortfolioDocumentsQuery(baseData.id);

	if (isDocumentsLoading) {
		return <DetailSectionLoader />;
	}

	const portfolioData = baseData.data as ServiceDetailDataDTO;

	const commodity =
		portfolioData.commodityService &&
		portfolioData.commodityService !==
			ClientPortfolioDataCommodityServiceEnum.None
			? t(PageResources.CommodityService[portfolioData.commodityService])
			: EMPTY_DETAIL_FIELD;

	const depositAmount = portfolioData?.depositAmount
		? getPortfolioFrequencyValue(
				portfolioData?.depositAmount,
				currencySign,
				portfolioData.paymentFrequency ?? null,
				t,
			)
		: EMPTY_DETAIL_FIELD;

	const fixedPrice = portfolioData?.fixedPriceByFrequency
		? getPortfolioFrequencyValue(
				portfolioData?.fixedPriceByFrequency,
				currencySign,
				portfolioData.paymentFrequency ?? null,
				t,
			)
		: EMPTY_DETAIL_FIELD;

	const bottomComponent =
		baseData.signedAt || baseData.startAt || baseData.endAt || depositAmount ? (
			<Stack>
				<DataRow
					label={t(PageResources.AdditionalInfo.signDate)}
					value={dateFormatter(baseData.signedAt)}
				/>
				<DataRow
					label={t(PageResources.AdditionalInfo.beginDate)}
					value={dateFormatter(baseData.startAt)}
				/>
				<DataRow
					label={t(PageResources.AdditionalInfo.endDate)}
					value={dateFormatter(baseData.endAt)}
				/>
				<DataRow
					label={t(PageResources.AdditionalInfo.depositAmount)}
					value={depositAmount}
				/>
			</Stack>
		) : null;

	return (
		<>
			<DataCard
				preset={DataCardPreset.BasicInfo}
				bottomComponent={bottomComponent}
			>
				<DataRow label={t(PageResources.name)} value={baseData.clientName} />
				<DataRow
					label={t(PageResources.contractNumber)}
					value={baseData?.contractNumber}
				/>
				<DataRow
					label={t(PageResources.producent)}
					value={baseData?.producer}
				/>
				<DataRow label={t(PageResources.product)} value={baseData?.product} />
			</DataCard>

			<DataCard
				label={t(PageResources.cardLabel)}
				icon={<Box size={16} color={theme.palette.deprecated.grey[700]} />}
				noDivider={
					!computeRelatedPresence(documents?.data, baseData.related).hasAny
				}
			>
				<DataRow
					label={t(PageResources.tariff)}
					value={portfolioData?.tariff}
				/>
				<DataRow label={t(PageResources.commodity)} value={commodity} />
				<DataRow
					label={t(PageResources.pricePerUnit, {
						unit: portfolioData?.unitOfMeasurement,
					})}
					value={moneyFormatter(portfolioData.pricePerUnit, currencySign)}
				/>
				<DataRow label={t(PageResources.fixedPrice)} value={fixedPrice} />
			</DataCard>

			<RelatedSection
				documents={documents?.data}
				related={baseData.related}
				portfolioId={baseData.id}
			/>
		</>
	);
};
